import { useMutation } from '@apollo/client';
import type { GraphQLError } from 'graphql';
import { useCallback } from 'react';

import type {
  CreateAdsWithCreativesMutation,
  CreateAdsWithCreativesMutationVariables,
} from '../../../../../../../apis/graphql/types';
import { getErrorMessageFromGraphQlErrors } from '../../../../../../../apis/graphql/utils';
import { AlertType } from '../../../../../../../common/Alert';
import { openToastAlert } from '../../../../../../../common/ToastAlert/toastAlert';
import { showFailure } from '../../../../../../../common/utils';
import { useGetCreativeLibraryIdByOrganizationId } from '../../../../../../../hooks/useGetCreativeLibraryIdByOrganizationId';
import { prependCreatedAdsToTraffickingConnections } from '../../../../../TraffickingPage/hooks/utils/prependCreatedAdsToTraffickingConnections';
import type { TagSheetParseResult } from '../../../types';
import { useUploadTagSheetContext } from '../../../UploadTagSheetStepperProvider/UploadTagSheetStepperProvider';
import { SAVE_DISABLED_TOOLTIP } from '../../../validation/constants';
import { validateLineItems } from '../../../validation/validateLineItems';
import {
  TAG_SHEET_SAVE_FAILURE_DESCRIPTION,
  TAG_SHEET_SAVE_FAILURE_TITLE,
  TAG_SHEET_SAVE_SUCCESS_TOAST,
  TAG_SHEET_SAVE_ZERO_SUCCESS_TOAST_DESCRIPTION,
  TAG_SHEET_SAVE_ZERO_SUCCESS_TOAST_TITLE,
} from './constants';
import { CREATE_ADS_WITH_CREATIVES } from './mutations/createCreativeWithAds';
import { getSuccessfullyCreatedAds, prepareCreateAdsWithCreativesInput } from './utils';

type UseSaveTagSheetAdsResult = {
  save: () => Promise<void>;
  loading: boolean;
};

export const useSaveTagSheetAds = (parseResult: TagSheetParseResult | null): UseSaveTagSheetAdsResult => {
  const { setSavedTagSheetResult, goToNextStep } = useUploadTagSheetContext();

  const organizationId = parseResult?.lineItems[0]?.campaign?.adAccount?.organizationId ?? null;
  const { creativeLibraryId, loading: loadingCreativeLibraryId } = useGetCreativeLibraryIdByOrganizationId({
    organizationId,
  });

  const [createAdsWithCreatives, { loading: mutationLoading }] = useMutation<
    CreateAdsWithCreativesMutation,
    CreateAdsWithCreativesMutationVariables
  >(CREATE_ADS_WITH_CREATIVES);

  const loading = mutationLoading || loadingCreativeLibraryId;

  const handleCreateAdsWithCreativesResult = useCallback(
    (
      data: CreateAdsWithCreativesMutation | null | undefined,
      errors: ReadonlyArray<GraphQLError> | undefined
    ): void => {
      if (!data?.createAdsWithCreatives) {
        showFailure({
          message: TAG_SHEET_SAVE_FAILURE_TITLE,
          description: errors?.length ? getErrorMessageFromGraphQlErrors(errors) : TAG_SHEET_SAVE_FAILURE_DESCRIPTION,
        });

        return;
      }

      const result = data.createAdsWithCreatives;
      const createdAds = getSuccessfullyCreatedAds(result);

      setSavedTagSheetResult(result);
      goToNextStep();

      if (createdAds.length >= 1) {
        openToastAlert({
          alertType: AlertType.SUCCESS,
          message: TAG_SHEET_SAVE_SUCCESS_TOAST,
        });
      } else {
        showFailure({
          message: TAG_SHEET_SAVE_ZERO_SUCCESS_TOAST_TITLE,
          description: TAG_SHEET_SAVE_ZERO_SUCCESS_TOAST_DESCRIPTION,
        });
      }
    },
    [setSavedTagSheetResult, goToNextStep]
  );

  const save = useCallback(async (): Promise<void> => {
    if (!parseResult) {
      return;
    }

    const { canSave } = validateLineItems(parseResult);

    if (!canSave) {
      showFailure({ message: 'Validation failed', description: SAVE_DISABLED_TOOLTIP });

      return;
    }

    const createAdsWithCreativesInput = prepareCreateAdsWithCreativesInput(parseResult, creativeLibraryId);

    if (!createAdsWithCreativesInput) {
      showFailure({ message: 'Validation failed', description: SAVE_DISABLED_TOOLTIP });

      return;
    }

    try {
      const { data, errors } = await createAdsWithCreatives({
        variables: createAdsWithCreativesInput,
        update(cache, { data: mutationData }) {
          const result = mutationData?.createAdsWithCreatives;

          if (!result) {
            return;
          }

          prependCreatedAdsToTraffickingConnections(cache, getSuccessfullyCreatedAds(result));
        },
      });

      handleCreateAdsWithCreativesResult(data, errors);
    } catch {
      showFailure({
        message: TAG_SHEET_SAVE_FAILURE_TITLE,
        description: TAG_SHEET_SAVE_FAILURE_DESCRIPTION,
      });
    }
  }, [parseResult, createAdsWithCreatives, handleCreateAdsWithCreativesResult, creativeLibraryId]);

  return { save, loading };
};
